import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  Eye, EyeOff, Loader2, 
  ArrowRight, Clock, ExternalLink, Phone, Globe, Mail,
  Play, KeyRound, Send
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';

// =============================================
// MSC MANAGER - PAGE DE CONNEXION
// Logo plein, horaires dynamiques, Vidéo YouTube configurable
// =============================================

// Helper to extract YouTube video ID
const extractYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/);
  return match ? match[1] : null;
};

// Noms des jours en français
const DAY_NAMES = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
  saturday: 'Samedi',
  sunday: 'Dimanche'
};

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Forgot password modal state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  
  // Theme and agency data
  const [themeData, setThemeData] = useState({
    colors: { primary: '#1a365d', accent: '#00d4aa' },
    logo: null,
    agency: { name: 'MSC Manager', phone: '+33 1 23 45 67 89', website: 'agence-msc.fr', login_video_url: '' },
    schedule: null
  });

  // Video state
  const [videoId, setVideoId] = useState(null);

  useEffect(() => {
    loadThemeData();
  }, []);

  const loadThemeData = async () => {
    try {
      const [themeRes, agencyRes, hoursRes] = await Promise.all([
        api.get('/settings/theme').catch(() => ({ data: null })),
        api.get('/settings/agency-info').catch(() => ({ data: null })),
        api.get('/settings/opening-hours').catch(() => ({ data: null }))
      ]);
      
      const agency = agencyRes.data || { name: 'MSC Manager', phone: '+33 1 23 45 67 89', website: 'agence-msc.fr', login_video_url: '' };
      
      // Use opening hours from dedicated endpoint, fallback to theme schedule
      const openingHours = hoursRes.data?.regular || themeRes.data?.schedule?.regular || null;
      
      setThemeData({
        colors: themeRes.data?.colors || { primary: '#1a365d', accent: '#00d4aa' },
        logo: themeRes.data?.logo,
        agency: agency,
        schedule: openingHours ? { regular: openingHours } : (themeRes.data?.schedule || null)
      });
      
      // Extract video ID if URL is provided
      if (agency.login_video_url) {
        const id = extractYouTubeId(agency.login_video_url);
        setVideoId(id);
      }
    } catch (error) {
      console.error('Error loading theme data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(identifier, password);
      toast.success(`Bienvenue, ${user.first_name || user.username} !`);
      redirectUser(user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  const redirectUser = (user) => {
    if (user.account_status === 'pending_approval') {
      navigate('/pending-approval', { replace: true });
      return;
    }
    
    switch (user.role) {
      case 'agency':
        navigate('/dashboard/agency', { replace: true });
        break;
      case 'affiliate':
        navigate('/affiliate/dashboard', { replace: true });
        break;
      case 'team_member':
        if (user.team_member_type === 'affiliate') {
          navigate('/affiliate/dashboard', { replace: true });
        } else {
          navigate('/dashboard/team', { replace: true });
        }
        break;
      case 'customer_service':
        navigate('/dashboard/customer-service', { replace: true });
        break;
      default:
        navigate('/dashboard/client', { replace: true });
    }
  };

  // Handle forgot password
  const handleForgotPassword = async () => {
    if (!resetEmail) {
      toast.error('Veuillez entrer votre adresse email');
      return;
    }
    
    setSendingReset(true);
    try {
      await api.post('/auth/forgot-password', { email: resetEmail });
      toast.success('Un email de réinitialisation a été envoyé à ' + resetEmail);
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      console.error('Forgot password error:', error);
      // Show success anyway to prevent email enumeration
      toast.success('Si cette adresse existe, un email de réinitialisation a été envoyé');
      setShowForgotPassword(false);
      setResetEmail('');
    } finally {
      setSendingReset(false);
    }
  };

  // Format schedule for display - group similar days
  // Handles both formats: 
  // - Simple: { open, close, closed }
  // - Morning/Afternoon: { morning: { open, close }, afternoon: { open, close }, closed }
  const formatSchedule = () => {
    const schedule = themeData.schedule?.regular;
    if (!schedule) {
      // Default schedule if none configured
      return [
        { days: 'Lundi - Vendredi', hours: '09:00 - 18:00', closed: false },
        { days: 'Samedi', hours: 'Fermé', closed: true },
        { days: 'Dimanche', hours: 'Fermé', closed: true }
      ];
    }

    // Helper to format hours for a day (handles both formats)
    const formatDayHours = (daySchedule) => {
      if (!daySchedule || daySchedule.closed) {
        return { hours: 'Fermé', closed: true };
      }
      
      // Check if it's the morning/afternoon format
      if (daySchedule.morning) {
        const morningOpen = daySchedule.morning?.open;
        const morningClose = daySchedule.morning?.close;
        const afternoonOpen = daySchedule.afternoon?.open;
        const afternoonClose = daySchedule.afternoon?.close;
        
        // If both morning and afternoon are set
        if (morningOpen && afternoonClose) {
          // If continuous (morning close == afternoon open or no break)
          if (!afternoonOpen || morningClose === afternoonOpen) {
            return { hours: `${morningOpen} - ${afternoonClose}`, closed: false };
          }
          // If there's a break
          return { hours: `${morningOpen} - ${morningClose} / ${afternoonOpen} - ${afternoonClose}`, closed: false };
        }
        // Only morning
        if (morningOpen && morningClose) {
          return { hours: `${morningOpen} - ${morningClose}`, closed: false };
        }
        // Only afternoon  
        if (afternoonOpen && afternoonClose) {
          return { hours: `${afternoonOpen} - ${afternoonClose}`, closed: false };
        }
        return { hours: 'Fermé', closed: true };
      }
      
      // Simple format { open, close }
      if (daySchedule.open && daySchedule.close) {
        return { hours: `${daySchedule.open} - ${daySchedule.close}`, closed: false };
      }
      
      return { hours: 'Fermé', closed: true };
    };

    const result = [];
    const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    
    // Get formatted hours for each weekday
    const weekdayFormatted = weekdays.map(d => ({
      day: d,
      ...formatDayHours(schedule[d])
    }));
    
    // Check if all weekdays have same hours
    const allWeekdaysSame = weekdayFormatted.every(h => 
      h.hours === weekdayFormatted[0].hours && 
      h.closed === weekdayFormatted[0].closed
    );

    if (allWeekdaysSame && !weekdayFormatted[0].closed) {
      result.push({
        days: 'Lundi - Vendredi',
        hours: weekdayFormatted[0].hours,
        closed: false
      });
    } else {
      // List each weekday individually (only non-closed days)
      weekdayFormatted.forEach(({ day, hours, closed }) => {
        if (!closed) {
          result.push({
            days: DAY_NAMES[day],
            hours,
            closed: false
          });
        }
      });
    }

    // Saturday
    const saturdayFormatted = formatDayHours(schedule.saturday);
    result.push({
      days: 'Samedi',
      hours: saturdayFormatted.hours,
      closed: saturdayFormatted.closed
    });

    // Sunday
    const sundayFormatted = formatDayHours(schedule.sunday);
    result.push({
      days: 'Dimanche',
      hours: sundayFormatted.hours,
      closed: sundayFormatted.closed
    });

    return result;
  };

  // Get website URL (ensure it has protocol)
  const getWebsiteUrl = () => {
    const website = themeData.agency?.website || 'agence-msc.fr';
    if (website.startsWith('http://') || website.startsWith('https://')) {
      return website;
    }
    return `https://${website}`;
  };

  const getWebsiteDisplay = () => {
    const website = themeData.agency?.website || 'agence-msc.fr';
    return website.replace(/^https?:\/\//, '').replace(/^www\./, '');
  };

  const primaryColor = themeData.colors?.primary || '#1a365d';
  const accentColor = themeData.colors?.accent || '#00d4aa';
  const scheduleItems = formatSchedule();

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding avec Logo, Slogan et Vidéo YouTube optionnelle */}
      <div 
        className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden flex-col items-center justify-center"
        style={{ backgroundColor: primaryColor }}
      >
        {/* YouTube Video Background (if configured) */}
        {videoId && (
          <div className="absolute inset-0 z-0 overflow-hidden">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
              title="Background Video"
              className="absolute top-1/2 left-1/2 w-[200%] h-[200%] -translate-x-1/2 -translate-y-1/2 pointer-events-none"
              style={{ 
                minWidth: '100%', 
                minHeight: '100%',
                border: 'none'
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            {/* Blue overlay at 85% for readability */}
            <div 
              className="absolute inset-0" 
              style={{ backgroundColor: primaryColor, opacity: 0.85 }}
            />
          </div>
        )}

        {/* Background Pattern (fallback when no video) */}
        {!videoId && (
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full" style={{ backgroundColor: accentColor, filter: 'blur(100px)' }} />
            <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-white" style={{ filter: 'blur(80px)' }} />
          </div>
        )}

        {/* Content - Centered */}
        <div className="relative z-10 flex flex-col items-center text-center px-8 lg:px-12 max-w-2xl">
          {/* Logo dans un rond avec effet de faisceau subtil */}
          <div className="relative w-36 h-36 lg:w-44 lg:h-44 mb-8">
            {/* Rotating beam effect - visible around logo */}
            <div 
              className="absolute inset-0 rounded-full"
              style={{
                background: `conic-gradient(from 0deg, transparent 0%, transparent 85%, ${accentColor} 92%, #38bdf8 96%, transparent 100%)`,
                animation: 'spin-clockwise 3s linear infinite',
                padding: '3px',
              }}
            >
              {/* Inner circle to create the beam effect border */}
              <div className="w-full h-full rounded-full" style={{ backgroundColor: primaryColor }} />
            </div>
            {/* Inner logo container - overlaid on top, no border */}
            <div 
              className="absolute inset-[3px] rounded-full bg-white flex items-center justify-center overflow-hidden"
              style={{ boxShadow: 'none' }}
            >
              {themeData.logo?.darkUrl ? (
                <img 
                  src={themeData.logo.darkUrl} 
                  alt="Logo" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <span 
                  className="text-4xl lg:text-5xl font-black"
                  style={{ color: primaryColor }}
                >
                  MSC
                </span>
              )}
            </div>
          </div>

          {/* Texte "Manager" en grand */}
          <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 tracking-tight">
            Manager
          </h1>

          {/* Slogan */}
          <p className="text-xl lg:text-2xl text-white/90 leading-relaxed mb-12 font-light">
            Gérez votre communication à 360°<br />
            <span className="text-white/70">avec une équipe dédiée et externalisée</span>
          </p>

          {/* Horaires d'ouverture - DYNAMIQUES depuis les paramètres */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 w-full max-w-md">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Clock className="w-5 h-5" style={{ color: accentColor }} />
              <span className="text-white font-semibold">Horaires d&apos;ouverture</span>
            </div>
            <div className="space-y-2 text-white/80 text-sm">
              {scheduleItems.map((item, idx) => (
                <div key={idx} className="flex justify-between">
                  <span>{item.days}</span>
                  <span className={item.closed ? "text-white/50" : "font-medium text-white"}>
                    {item.hours}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Lien vers le site vitrine - URL CONFIGURABLE */}
          <a 
            href={getWebsiteUrl()} 
            target="_blank" 
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors group"
          >
            <Globe className="w-4 h-4" />
            <span>{getWebsiteDisplay()}</span>
            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>
        </div>

        {/* Footer */}
        <div className="absolute bottom-8 left-0 right-0 text-center">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} MSC L&apos;Agence - Tous droits réservés
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="w-full max-w-md">
          {/* Mobile Header - Logo et titre */}
          <div className="lg:hidden text-center mb-8">
            {/* Logo mobile dans un rond - SANS contour */}
            <div className="w-24 h-24 rounded-full bg-white shadow-lg mx-auto mb-4 flex items-center justify-center overflow-hidden">
              {themeData.logo?.darkUrl ? (
                <img 
                  src={themeData.logo.darkUrl} 
                  alt="Logo" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <span 
                  className="text-2xl font-black"
                  style={{ color: primaryColor }}
                >
                  MSC
                </span>
              )}
            </div>
            <h1 className="text-3xl font-bold mb-2" style={{ color: primaryColor }}>
              Manager
            </h1>
            <p className="text-slate-500 text-sm px-4">
              Gérez votre communication à 360° avec une équipe dédiée
            </p>
          </div>

          {/* Login Card */}
          <Card className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border-0">
            <CardContent className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold" style={{ color: primaryColor }}>
                  Connexion
                </h2>
                <p className="text-slate-500 text-sm">
                  Accédez à votre espace de travail
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="identifier" style={{ color: primaryColor }}>
                    Email
                  </Label>
                  <Input
                    id="identifier"
                    type="email"
                    placeholder="votre@email.com"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    className="h-11 rounded-xl"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" style={{ color: primaryColor }}>
                    Mot de passe
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-11 rounded-xl pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded" />
                    <span className="text-slate-600">Se souvenir</span>
                  </label>
                  <button 
                    type="button"
                    onClick={() => setShowForgotPassword(true)} 
                    className="hover:underline" 
                    style={{ color: accentColor }}
                  >
                    Mot de passe oublié ?
                  </button>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 rounded-xl font-medium text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Se connecter
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              {/* Demo Accounts Section */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-xs text-slate-500 text-center mb-3">
                  🧪 Comptes de démonstration
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs rounded-lg border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300"
                    onClick={() => {
                      setIdentifier('manager@agence-msc.fr');
                      setPassword('MSCpass!13!26');
                    }}
                  >
                    <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold mr-1.5">A</span>
                    Agence
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs rounded-lg border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300"
                    onClick={() => {
                      setIdentifier('contact@besson-solutions.fr');
                      setPassword('demo123');
                    }}
                  >
                    <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-[10px] font-bold mr-1.5">C</span>
                    Client
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs rounded-lg border-amber-200 hover:bg-amber-50 hover:border-amber-300"
                    onClick={() => {
                      setIdentifier('contact@etreotop.fr');
                      setPassword('demo123');
                    }}
                  >
                    <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-[10px] font-bold mr-1.5">C</span>
                    Client 2
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs rounded-lg border-purple-200 hover:bg-purple-50 hover:border-purple-300"
                    onClick={() => {
                      setIdentifier('demo-affiliate@msc.fr');
                      setPassword('demo123');
                    }}
                  >
                    <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-[10px] font-bold mr-1.5">AP</span>
                    Apporteur
                  </Button>
                </div>
                <p className="text-[10px] text-slate-400 text-center mt-2">
                  Cliquez pour pré-remplir, puis "Se connecter"
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Contact Support - Téléphone et Email */}
          <div className="mt-6 text-center space-y-3">
            {/* Numéro de téléphone */}
            <a 
              href="tel:+33484492700"
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <Phone className="w-4 h-4" style={{ color: accentColor }} />
              </div>
              <span className="font-semibold">04 84 49 27 00</span>
            </a>
            
            {/* Email de support */}
            <div>
              <a 
                href="mailto:support@agence-msc.fr"
                className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span className="text-sm">support@agence-msc.fr</span>
              </a>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-slate-400 text-xs">
              © {new Date().getFullYear()} MSC Manager
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: primaryColor }}>
              <KeyRound className="w-5 h-5" />
              Mot de passe oublié
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Entrez votre adresse email pour recevoir un lien de réinitialisation de mot de passe.
            </p>
            <div className="space-y-2">
              <Label htmlFor="reset-email">Email</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="votre@email.com"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <Button
              onClick={handleForgotPassword}
              disabled={sendingReset || !resetEmail}
              className="w-full h-11 rounded-xl font-medium text-white"
              style={{ backgroundColor: primaryColor }}
            >
              {sendingReset ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer le lien
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
